import React from 'react'

import {
  useBrandColor,
  useBrandGraphic,
} from '../../../../Designs/blocs/brandResolvers'
import { useLastActiveMediaTrayCard } from '../../../../firebase/hooks/useTray'
import type { Feed, FeedMedia, Mix } from '../../../../firebase/model/mix'
import type { DeepReadonly } from '../../../../firebase/typeUtils'
import { VideoTrack } from '../../../../MediaTracks/components/VideoTrack/VideoTrack'
import { FloorTrayImage } from '../../../../Stage/floor/FloorFeed/FloorTrayFeed/FloorTrayImage/FloorTrayImage'
import { StaticElementsForMix } from '../../../../Stage/floor/StaticElements/StaticElements'
import { COVER_ZOOM, posToStyle } from '../../../../Stage/layouts'
import {
  useHeadlessParticipant,
  useParticipant,
} from '../../../../webrtc/webRTCRoom'

import './MixPreview.scss'

interface LayoutPreviewProps {
  mix: DeepReadonly<Mix>
}
const MixPreview = ({ mix }: LayoutPreviewProps) => {
  const toBrandColor = useBrandColor()
  const toBrandGraphic = useBrandGraphic()

  const backgroundGraphic = toBrandGraphic(mix.settings?.backgroundImage)

  return (
    <div
      className="mix-preview"
      style={{
        '--mix-background-color': toBrandColor(mix.settings?.backgroundColor),
        backgroundImage: backgroundGraphic
          ? `url(${backgroundGraphic})`
          : undefined,
      }}
    >
      <div>
        {mix.feeds.map((f) => {
          const aspectRatio = ((f.position.w / f.position.h) * 16) / 9
          const absRatio = aspectRatio < 1 ? 1 / aspectRatio : aspectRatio
          return (
            <div
              className="mix-preview-feed-positioner"
              key={!f.media ? f.index : `${f.media.type}-${f.media.id}`}
              style={{
                ...posToStyle(f),
                '--aspect-ratio': aspectRatio,
                '--abs-ratio': absRatio,
                '--zoomOut':
                  f.crop.zoom < COVER_ZOOM ? 1 - f.crop.zoom / COVER_ZOOM : 0,
                '--zoomIn':
                  f.crop.zoom > COVER_ZOOM
                    ? (f.crop.zoom - COVER_ZOOM) / (100 - COVER_ZOOM)
                    : 0,
                '--centerX': f.crop.centerX,
                '--centerY': f.crop.centerY,
              }}
            >
              <Track track={f.media} crop={f.crop} />
            </div>
          )
        })}
        <StaticElementsForMix mix={mix} />
      </div>
    </div>
  )
}

type MimicCrop = DeepReadonly<Feed['crop']>

const Track = ({
  track,
  crop,
}: {
  track: DeepReadonly<FeedMedia> | undefined
  crop: MimicCrop
}) => {
  if (!track) return null
  if (track.type === 'tray') {
    return <TrayTrack crop={crop} />
  }
  return (
    <ParticipantTrack type={track.type} participantId={track.id} crop={crop} />
  )
}

const ParticipantTrack = ({
  type,
  participantId,
  crop,
}: {
  type: 'camera' | 'screen' | 'mobile'
  participantId: number
  crop: MimicCrop
}) => {
  const participant = useParticipant(participantId)

  if (!participant) return null

  const mediaTrack = participant.tracks[type]

  if (!mediaTrack) return null

  return <VideoTrack track={mediaTrack} isMimic crop={crop} />
}

const TrayTrack = ({ crop }: { crop: MimicCrop }) => {
  const lastActiveMediaCard = useLastActiveMediaTrayCard()

  const headlessParticipant = useHeadlessParticipant()

  return (
    <div className="tray-track">
      {lastActiveMediaCard?.type === 'image' ? (
        <FloorTrayImage trayCard={lastActiveMediaCard} />
      ) : (
        <>
          {lastActiveMediaCard?.type === 'video' &&
            headlessParticipant?.tracks.screen && (
              <VideoTrack
                track={headlessParticipant.tracks.screen}
                isMimic
                crop={crop}
              />
            )}
        </>
      )}
    </div>
  )
}

export { MixPreview }
